/**
 * WeaveIn.ai Platform - Member Workforce Profile Tab
 * Displays workforce-specific data for members who are workforce candidates
 */

import React, { useState, useEffect } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  Briefcase,
  GraduationCap,
  Award,
  Users,
  MapPin,
  Clock,
  User,
  Target,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Calendar,
  Languages,
  Heart,
  Loader2,
  Plus,
  UserPlus,
  BookOpen,
  ScrollText,
  CalendarCheck,
  ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { apiClient } from '@/services/apiClient';

interface WorkforceProfile {
  id: number;
  member_id: number;
  cbo_id: number;
  education_level?: string;
  current_occupation?: string;
  years_experience?: number;
  skills?: string[];
  languages?: string[];
  community_knowledge_areas?: string[];
  sdoh_lived_experience?: Record<string, any>;
  recruitment_status?: string;
  pipeline_stage?: string;
  overall_match_score?: number;
  skills_gap_match_score?: number;
  community_fit_score?: number;
  assigned_recruiter_id?: number;
  assigned_recruiter_name?: string;
  target_role_id?: number;
  target_role_name?: string;
  target_program_id?: number;
  target_program_name?: string;
  last_contacted_at?: string;
  contact_attempts?: number;
  converted_to_user_id?: number;
  converted_at?: string;
  source_type?: string;
  discovered_via?: string;
  created_at: string;
  updated_at: string;
}

interface MemberWorkforceTabProps {
  memberId: number;
}

// ---- Unified Training Record ("My Learning") ----
interface LearnerRecordSummary {
  courses_completed?: number;
  curricula_completed?: number;
  certifications?: number;
  sessions_attended?: number;
}

interface LearnerLmsCourse {
  title?: string;
  status?: string;
  completed_at?: string;
}

interface LearnerCurriculum {
  curriculum_name?: string;
  status?: string;
  progress_percentage?: number;
  final_exam_passed?: boolean;
  completed_at?: string;
}

interface LearnerCertification {
  name?: string;
  status?: string;
  certificate_number?: string;
  certification_date?: string;
  expiration_date?: string;
  certificate_url?: string;
}

interface LearnerAttendance {
  series_title?: string;
  session_title?: string;
  start_time?: string;
  attended?: boolean;
  attendance_mode?: string;
}

interface LearnerRecord {
  member_id?: number;
  user_id?: number;
  summary?: LearnerRecordSummary;
  lms_courses?: LearnerLmsCourse[];
  curricula?: LearnerCurriculum[];
  certifications?: LearnerCertification[];
  training_attendance?: LearnerAttendance[];
}

// Pipeline stage configuration
const PIPELINE_STAGES = [
  { key: 'awareness', label: 'Awareness', order: 1 },
  { key: 'interest', label: 'Interest', order: 2 },
  { key: 'consideration', label: 'Consideration', order: 3 },
  { key: 'intent', label: 'Intent', order: 4 },
  { key: 'evaluation', label: 'Evaluation', order: 5 },
  { key: 'onboarded', label: 'Onboarded', order: 6 },
];

// Recruitment status badge colors
const STATUS_COLORS: Record<string, string> = {
  prospect: 'bg-gray-100 text-gray-800',
  contacted: 'bg-blue-100 text-blue-800',
  interested: 'bg-cyan-100 text-cyan-800',
  screening: 'bg-yellow-100 text-yellow-800',
  accepted: 'bg-green-100 text-green-800',
  enrolled: 'bg-purple-100 text-purple-800',
  declined: 'bg-red-100 text-red-800',
  inactive: 'bg-gray-100 text-gray-500',
};

// Education level options
const EDUCATION_LEVELS = [
  'High School',
  'Some College',
  'Associate Degree',
  'Bachelor\'s Degree',
  'Master\'s Degree',
  'Doctorate',
  'Professional Certification',
  'Other',
];

// Source type options
const SOURCE_TYPES = [
  { value: 'application', label: 'Direct Application' },
  { value: 'referral', label: 'Referral' },
  { value: 'outreach', label: 'Outreach Campaign' },
  { value: 'skills_gap_agent', label: 'Skills Gap Analysis' },
  { value: 'mentoring_program', label: 'Mentoring Program' },
  { value: 'existing_staff', label: 'Existing Staff' },
  { value: 'community_member', label: 'Community Member' },
];

export default function MemberWorkforceTab({ memberId }: MemberWorkforceTabProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [profile, setProfile] = useState<WorkforceProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Form state for creating profile
  const [formData, setFormData] = useState({
    education_level: '',
    current_occupation: '',
    years_experience: 0,
    skills: '',
    languages: '',
    recruitment_status: 'prospect',
    pipeline_stage: 'awareness',
    source_type: 'existing_staff',
    discovered_via: '',
    notes: '',
  });

  useEffect(() => {
    fetchWorkforceProfile();
  }, [memberId]);

  const fetchWorkforceProfile = async () => {
    setLoading(true);
    setError(null);

    try {
      // Use shared apiClient so 401s trigger automatic token refresh
      // (raw axios bypasses the refresh interceptor and surfaces "invalid token")
      const response = await apiClient.get(`/api/members/${memberId}/workforce-profile`);
      setProfile(response.data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        // No workforce profile - this is okay, member isn't a workforce candidate
        setProfile(null);
      } else {
        setError(err.response?.data?.detail || 'Failed to load workforce profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProfile = async () => {
    setCreating(true);
    setCreateError(null);

    try {
      // Parse skills and languages as arrays
      const skillsArray = formData.skills
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);
      const languagesArray = formData.languages
        .split(',')
        .map(l => l.trim())
        .filter(l => l.length > 0);

      const payload = {
        education_level: formData.education_level || null,
        current_occupation: formData.current_occupation || null,
        years_experience: formData.years_experience || 0,
        skills: skillsArray,
        languages: languagesArray,
        recruitment_status: formData.recruitment_status,
        pipeline_stage: formData.pipeline_stage,
        source_type: formData.source_type,
        discovered_via: formData.discovered_via || null,
        notes: formData.notes || null,
      };

      await apiClient.put(`/api/members/${memberId}/workforce-profile`, payload);

      setShowCreateDialog(false);
      await fetchWorkforceProfile();
    } catch (err: any) {
      setCreateError(err.response?.data?.detail || 'Failed to create workforce profile');
    } finally {
      setCreating(false);
    }
  };

  const getCurrentPipelineStageIndex = () => {
    if (!profile?.pipeline_stage) return -1;
    return PIPELINE_STAGES.findIndex(s => s.key === profile.pipeline_stage);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return fmt.medium(dateStr);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-500">Loading workforce profile...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!profile) {
    return (
      <>
        <div className="text-center py-12">
          <Briefcase className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Workforce Profile
          </h3>
          <p className="text-gray-500 mb-6">
            This member does not have an associated workforce profile.
          </p>
          <Button onClick={() => setShowCreateDialog(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Create Workforce Profile
          </Button>
        </div>

        {/* Create Workforce Profile Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Create Workforce Profile
              </DialogTitle>
              <DialogDescription>
                Add workforce management data to this member's profile.
              </DialogDescription>
            </DialogHeader>

            {createError && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{createError}</AlertDescription>
              </Alert>
            )}

            <div className="grid gap-4 py-4">
              {/* Background Section */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
                  <GraduationCap className="h-4 w-4" />
                  Background & Experience
                </h4>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="education_level">Education Level</Label>
                    <Select
                      value={formData.education_level}
                      onValueChange={(value) => setFormData({ ...formData, education_level: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select education level" />
                      </SelectTrigger>
                      <SelectContent>
                        {EDUCATION_LEVELS.map((level) => (
                          <SelectItem key={level} value={level}>{level}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="years_experience">Years of Experience</Label>
                    <Input
                      id="years_experience"
                      type="number"
                      min="0"
                      value={formData.years_experience}
                      onChange={(e) => setFormData({ ...formData, years_experience: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="current_occupation">Current Occupation</Label>
                  <Input
                    id="current_occupation"
                    value={formData.current_occupation}
                    onChange={(e) => setFormData({ ...formData, current_occupation: e.target.value })}
                    placeholder="e.g., Community Health Worker, Nurse, Social Worker"
                  />
                </div>
              </div>

              <Separator />

              {/* Skills & Languages */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  Skills & Languages
                </h4>

                <div className="space-y-2">
                  <Label htmlFor="skills">Skills (comma-separated)</Label>
                  <Input
                    id="skills"
                    value={formData.skills}
                    onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                    placeholder="e.g., Case Management, Patient Advocacy, Health Education"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="languages">Languages (comma-separated)</Label>
                  <Input
                    id="languages"
                    value={formData.languages}
                    onChange={(e) => setFormData({ ...formData, languages: e.target.value })}
                    placeholder="e.g., English, Spanish, Vietnamese"
                  />
                </div>
              </div>

              <Separator />

              {/* Recruitment Status */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Recruitment Status
                </h4>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="recruitment_status">Status</Label>
                    <Select
                      value={formData.recruitment_status}
                      onValueChange={(value) => setFormData({ ...formData, recruitment_status: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="prospect">Prospect</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="interested">Interested</SelectItem>
                        <SelectItem value="screening">Screening</SelectItem>
                        <SelectItem value="accepted">Accepted</SelectItem>
                        <SelectItem value="enrolled">Enrolled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pipeline_stage">Pipeline Stage</Label>
                    <Select
                      value={formData.pipeline_stage}
                      onValueChange={(value) => setFormData({ ...formData, pipeline_stage: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PIPELINE_STAGES.map((stage) => (
                          <SelectItem key={stage.key} value={stage.key}>{stage.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Source Tracking */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Source Information
                </h4>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="source_type">Source Type</Label>
                    <Select
                      value={formData.source_type}
                      onValueChange={(value) => setFormData({ ...formData, source_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SOURCE_TYPES.map((source) => (
                          <SelectItem key={source.value} value={source.value}>{source.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="discovered_via">Discovered Via</Label>
                    <Input
                      id="discovered_via"
                      value={formData.discovered_via}
                      onChange={(e) => setFormData({ ...formData, discovered_via: e.target.value })}
                      placeholder="e.g., Job fair, Community event, Website"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes about this workforce candidate..."
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateProfile} disabled={creating}>
                {creating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Profile
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  const currentStageIndex = getCurrentPipelineStageIndex();
  const pipelineProgress = currentStageIndex >= 0
    ? ((currentStageIndex + 1) / PIPELINE_STAGES.length) * 100
    : 0;

  return (
    <div className="space-y-6">
      {/* Conversion Notice */}
      {profile.converted_to_user_id && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            This member was converted to a staff user on {formatDate(profile.converted_at)}.
          </AlertDescription>
        </Alert>
      )}

      {/* Pipeline Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Recruitment Pipeline
          </CardTitle>
          <CardDescription>
            Current stage in the workforce recruitment process
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Status Badge */}
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">Status:</span>
              <Badge className={STATUS_COLORS[profile.recruitment_status || 'prospect']}>
                {(profile.recruitment_status || 'prospect').replace('_', ' ').toUpperCase()}
              </Badge>
            </div>

            {/* Pipeline Progress */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Pipeline Progress</span>
                <span className="font-medium">{Math.round(pipelineProgress)}%</span>
              </div>
              <Progress value={pipelineProgress} className="h-2" />
              <div className="flex justify-between text-xs text-gray-400">
                {PIPELINE_STAGES.map((stage, idx) => (
                  <span
                    key={stage.key}
                    className={idx <= currentStageIndex ? 'text-blue-600 font-medium' : ''}
                  >
                    {stage.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Match Scores Card */}
      {(profile.overall_match_score || profile.skills_gap_match_score || profile.community_fit_score) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Match Scores
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {profile.overall_match_score && (
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-blue-600">
                    {profile.overall_match_score}%
                  </div>
                  <div className="text-sm text-gray-500">Overall Match</div>
                </div>
              )}
              {profile.skills_gap_match_score && (
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-green-600">
                    {profile.skills_gap_match_score}%
                  </div>
                  <div className="text-sm text-gray-500">Skills Match</div>
                </div>
              )}
              {profile.community_fit_score && (
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-purple-600">
                    {profile.community_fit_score}%
                  </div>
                  <div className="text-sm text-gray-500">Community Fit</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Background & Experience */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5" />
              Background & Experience
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <span className="text-sm text-gray-500">Education Level</span>
              <p className="font-medium">{profile.education_level || 'Not specified'}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Current Occupation</span>
              <p className="font-medium">{profile.current_occupation || 'Not specified'}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Years of Experience</span>
              <p className="font-medium">
                {profile.years_experience !== undefined ? `${profile.years_experience} years` : 'Not specified'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Assignment & Target */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Assignment & Target
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <span className="text-sm text-gray-500">Assigned Recruiter</span>
              <p className="font-medium flex items-center gap-2">
                <User className="h-4 w-4 text-gray-400" />
                {profile.assigned_recruiter_name || 'Unassigned'}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Target Role</span>
              <p className="font-medium flex items-center gap-2">
                <Target className="h-4 w-4 text-gray-400" />
                {profile.target_role_name || 'Not assigned'}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Target Program</span>
              <p className="font-medium">
                {profile.target_program_name || 'Not assigned'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Skills & Languages */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Skills & Languages
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Skills */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Skills
              </h4>
              {profile.skills && profile.skills.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map((skill, idx) => (
                    <Badge key={idx} variant="secondary">
                      {skill}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No skills listed</p>
              )}
            </div>

            {/* Languages */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                <Languages className="h-4 w-4" />
                Languages
              </h4>
              {profile.languages && profile.languages.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {profile.languages.map((lang, idx) => (
                    <Badge key={idx} variant="outline">
                      {lang}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No languages listed</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Community Knowledge */}
      {profile.community_knowledge_areas && profile.community_knowledge_areas.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5" />
              Community Knowledge
            </CardTitle>
            <CardDescription>
              Areas of expertise based on lived experience and community involvement
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {profile.community_knowledge_areas.map((area, idx) => (
                <Badge key={idx} className="bg-purple-100 text-purple-800">
                  {area}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contact & Source Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Contact & Source Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <span className="text-sm text-gray-500">Last Contacted</span>
              <p className="font-medium">{formatDate(profile.last_contacted_at)}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Contact Attempts</span>
              <p className="font-medium">{profile.contact_attempts || 0}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Source Type</span>
              <p className="font-medium">{profile.source_type || 'Unknown'}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Discovered Via</span>
              <p className="font-medium">{profile.discovered_via || 'Not specified'}</p>
            </div>
          </div>
          <Separator className="my-4" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-500">
            <div>
              <span>Profile Created:</span>
              <span className="ml-2 font-medium">{formatDate(profile.created_at)}</span>
            </div>
            <div>
              <span>Last Updated:</span>
              <span className="ml-2 font-medium">{formatDate(profile.updated_at)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Unified Training Record ("My Learning") */}
      <MemberTrainingRecord memberId={memberId} />
    </div>
  );
}

// Status badge colors for training records
const TRAINING_STATUS_COLORS: Record<string, string> = {
  completed: 'bg-green-100 text-green-800',
  passed: 'bg-green-100 text-green-800',
  active: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-blue-100 text-blue-800',
  enrolled: 'bg-cyan-100 text-cyan-800',
  issued: 'bg-green-100 text-green-800',
  expired: 'bg-red-100 text-red-800',
  revoked: 'bg-red-100 text-red-800',
  pending: 'bg-yellow-100 text-yellow-800',
  not_started: 'bg-gray-100 text-gray-600',
};

function TrainingStatusBadge({ status }: { status?: string }) {
  const key = (status || '').toLowerCase();
  return (
    <Badge className={TRAINING_STATUS_COLORS[key] || 'bg-gray-100 text-gray-800'}>
      {(status || 'unknown').replace(/_/g, ' ').toUpperCase()}
    </Badge>
  );
}

/**
 * Unified Training Record — renders a person's LMS courses, curricula,
 * certifications and training attendance from the workforce learner-record API.
 * Self-contained (own hooks) so it can sit alongside the workforce profile.
 */
function MemberTrainingRecord({ memberId }: { memberId: number }) {
  const { fmt } = useFormattedDate();
  const [record, setRecord] = useState<LearnerRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openingCert, setOpeningCert] = useState<number | null>(null);

  // The certificate endpoint is authed (JWT + X-CBO-ID) and returns application/pdf,
  // so a plain new-tab navigation would send no auth headers → 401. Fetch the PDF
  // through the shared apiClient (which injects Authorization + X-CBO-ID) and open
  // the resulting blob in a new tab instead.
  const openCertificate = async (certUrl: string, idx: number) => {
    setOpeningCert(idx);
    let objectUrl: string | null = null;
    try {
      const res = await apiClient.get(certUrl, { responseType: 'blob' });
      objectUrl = URL.createObjectURL(res.data);
      window.open(objectUrl, '_blank', 'noopener');
      // Revoke after a delay so the new tab has time to load the blob.
      const toRevoke = objectUrl;
      setTimeout(() => URL.revokeObjectURL(toRevoke), 60000);
    } catch {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setError('Failed to open certificate');
    } finally {
      setOpeningCert(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(
          `/api/workforce/learner-record?member_id=${memberId}`,
        );
        if (!cancelled) setRecord(response.data || null);
      } catch (err: any) {
        if (cancelled) return;
        if (err.response?.status === 404) {
          setRecord(null);
        } else {
          setError(err.response?.data?.detail || 'Failed to load training record');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  const formatDate = (dateStr?: string) => (dateStr ? fmt.medium(dateStr) : 'N/A');

  const summary = record?.summary || {};
  const courses = record?.lms_courses || [];
  const curricula = record?.curricula || [];
  const certifications = record?.certifications || [];
  const attendance = record?.training_attendance || [];

  const summaryTiles = [
    { label: 'Courses Completed', value: summary.courses_completed ?? 0, color: 'text-blue-600' },
    { label: 'Curricula Completed', value: summary.curricula_completed ?? 0, color: 'text-purple-600' },
    { label: 'Certifications', value: summary.certifications ?? 0, color: 'text-green-600' },
    { label: 'Sessions Attended', value: summary.sessions_attended ?? 0, color: 'text-cyan-600' },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GraduationCap className="h-5 w-5" />
          Training Record
        </CardTitle>
        <CardDescription>
          Unified learning history — courses, curricula, certifications and training attendance
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-500">Loading training record...</span>
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-6">
            {/* Summary tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {summaryTiles.map((tile) => (
                <div key={tile.label} className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className={`text-3xl font-bold ${tile.color}`}>{tile.value}</div>
                  <div className="text-sm text-gray-500">{tile.label}</div>
                </div>
              ))}
            </div>

            {/* Certifications */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <ScrollText className="h-4 w-4" />
                Certifications
              </h4>
              {certifications.length > 0 ? (
                <div className="space-y-3">
                  {certifications.map((cert, idx) => (
                    <div
                      key={idx}
                      className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 p-3 border rounded-lg"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{cert.name || 'Certification'}</span>
                          <TrainingStatusBadge status={cert.status} />
                        </div>
                        <div className="text-xs text-gray-500 space-x-3">
                          {cert.certificate_number && (
                            <span>No. {cert.certificate_number}</span>
                          )}
                          <span>Issued: {formatDate(cert.certification_date)}</span>
                          <span>Expires: {formatDate(cert.expiration_date)}</span>
                        </div>
                      </div>
                      {cert.certificate_url && (
                        <button
                          type="button"
                          disabled={openingCert === idx}
                          onClick={() => openCertificate(cert.certificate_url as string, idx)}
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {openingCert === idx ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <ExternalLink className="h-4 w-4" />
                          )}
                          View certificate
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No certifications earned yet</p>
              )}
            </div>

            <Separator />

            {/* Courses */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Courses
              </h4>
              {courses.length > 0 ? (
                <div className="space-y-2">
                  {courses.map((course, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between gap-2 p-2 border-b last:border-b-0"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{course.title || 'Untitled course'}</span>
                        <TrainingStatusBadge status={course.status} />
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {course.completed_at ? formatDate(course.completed_at) : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No courses on record</p>
              )}
            </div>

            <Separator />

            {/* Curricula */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Award className="h-4 w-4" />
                Curricula
              </h4>
              {curricula.length > 0 ? (
                <div className="space-y-3">
                  {curricula.map((curr, idx) => (
                    <div key={idx} className="p-2 border-b last:border-b-0 space-y-2">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">
                            {curr.curriculum_name || 'Curriculum'}
                          </span>
                          <TrainingStatusBadge status={curr.status} />
                          {curr.final_exam_passed && (
                            <Badge variant="outline" className="text-green-700 border-green-300">
                              Final exam passed
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {Math.round(curr.progress_percentage ?? 0)}%
                        </span>
                      </div>
                      <Progress value={Math.min(100, Math.max(0, curr.progress_percentage ?? 0))} className="h-2" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No curricula on record</p>
              )}
            </div>

            <Separator />

            {/* Attendance */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <CalendarCheck className="h-4 w-4" />
                Training Attendance
              </h4>
              {attendance.length > 0 ? (
                <div className="space-y-2">
                  {attendance.map((att, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between gap-2 p-2 border-b last:border-b-0"
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">
                          {att.session_title || att.series_title || 'Session'}
                        </div>
                        {att.series_title && att.session_title && (
                          <div className="text-xs text-gray-400 truncate">{att.series_title}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        {att.attendance_mode && (
                          <Badge variant="outline" className="text-xs">
                            {att.attendance_mode}
                          </Badge>
                        )}
                        <Badge
                          className={
                            att.attended
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-600'
                          }
                        >
                          {att.attended ? 'Attended' : 'Absent'}
                        </Badge>
                        <span className="text-xs text-gray-500">{formatDate(att.start_time)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No training attendance on record</p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
